import { search } from "algolia-instant-search";
import { getYMMFitmentFilter } from "algolia-fitment-filter";
const { stats, configure } = instantsearch.widgets;

const PROD_STORE = "fbgure-nn.myshopify.com";
if (!customElements.get("main-collection")) {
  class MainCollection extends HTMLElement {
    collectionId = this.dataset.collectionId;
    collectionHandle = this.dataset.collectionHandle;
    breadcrumbStorageKey = "collection-product-breadcrumb-context";

    constructor() {
      super();
      if (!this.collectionId) {
        window.location.href = "/collections/all-products";
      }
    }

    async connectedCallback() {
      this.prepareThemeEditorSearch();

      if (window.Shopify.shop !== PROD_STORE && this.collectionHandle) {
        const prodId = await this.resolveProdCollectionId(
          this.collectionHandle,
        );
        if (prodId) this.collectionId = prodId;
      }

      document
        .querySelectorAll(".collection-description-content iframe")
        .forEach((iframe) => {
          if (!iframe.hasAttribute("loading"))
            iframe.setAttribute("loading", "lazy");
        });

      this.selectElements();
      this.initializeSearch();
      this.setupBreadcrumbContextHandoff();

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
          if (!search.started) search.start();
        });
      } else if (!search.started) {
        search.start();
      }
    }

    disconnectedCallback() {
      this.productGrid?.removeEventListener("click", this.handleProductClick);
    }

    prepareThemeEditorSearch() {
      if (!window.Shopify?.designMode) return;

      const previousElement = window.__dppMainCollectionElement;
      if (previousElement && previousElement !== this && search.started) {
        search.dispose();
      }

      window.__dppMainCollectionElement = this;
    }

    selectElements() {
      const tag = this.querySelector("script#data");
      try {
        this.data = JSON.parse(tag.textContent);
      } catch (error) {
        console.log(error);
        this.data = {};
      }
    }

    async resolveProdCollectionId(handle) {
      try {
        const mapUrl = this.dataset.collectionMapUrl;
        if (!mapUrl) return null;
        const res = await fetch(mapUrl);
        if (!res.ok) return null;
        const map = await res.json();
        return map[handle] || null;
      } catch {
        return null;
      }
    }

    initializeSearch() {
      search.addWidgets([this.statsWidget, this.configureWidget]);
    }

    get statsWidget() {
      return stats({
        container: "#stats",
        templates: {
          text(data) {
            const { nbHits, page, hitsPerPage } = data;

            if (nbHits === 0) return "No results";

            const first = page * hitsPerPage + 1;
            const last = Math.min((page + 1) * hitsPerPage, nbHits);

            return `${nbHits.toLocaleString()} results (showing ${first}-${last})`;
          },
        },
      });
    }

    get configureWidget() {
      const catalogIdsFilter = this.data?.catalog_gids
        ? `AND catalog_ids:${this.data.catalog_gids.join(",")}`
        : "";
      const collectionIdsFilter = this.collectionId
        ? `AND collection_ids:${this.collectionId}`
        : "";
      const ymmFitmentFilter = getYMMFitmentFilter(
        new URLSearchParams(window.location.search),
      );
      const baseFilters = `status:ACTIVE AND NOT metafield_seo_hidden:1 AND NOT metafield_product_availability:Discontinued ${collectionIdsFilter} ${catalogIdsFilter}`;
      const infiniteHits = this.querySelector("algolia-infinite-hits");
      if (infiniteHits) {
        infiniteHits.dataset.hasFitmentFilter = ymmFitmentFilter
          ? "true"
          : "false";
        infiniteHits.dataset.baseFilters = baseFilters;
        // Pass the raw fitment filter so empty-state recommendation rails can stay vehicle-scoped.
        infiniteHits.dataset.fitmentFilter = ymmFitmentFilter;
      }
      const catalogPriceKeys =
        this.data?.catalog_gids?.map((id) => `price_catalog_${id}`) ?? [];

      return configure({
        filters: `${baseFilters} ${ymmFitmentFilter}`,
        optionalFilters: [
          "popularity_tier:HIGH<score=3>",
          "popularity_tier:MID<score=2>",
          "is_quickship_stale:true<score=2>",
        ],
        sumOrFiltersScores: true,
        // WHY: with a vehicle fitment filter active, a query that matches nothing within that
        // vehicle's parts must NOT loosen — removeWordsIfNoResults strips the query to one word
        // and returns hundreds of wrong-fit junk. Force 0 → the "Clear Vehicle Fitment" empty
        // state instead. No-fitment searches keep the index-default recall behavior.
        ...(ymmFitmentFilter ? { removeWordsIfNoResults: "none" } : {}),
        distinct: true,
        maxValuesPerFacet: 1000,
        attributesToRetrieve: [
          "handle",
          "title",
          "product_title",
          "featured_image",
          "images",
          "price",
          "compare_at_price",
          "sku",
          "variants",
          "objectID",
          "product_id",
          "id",
          "variant_id",
          "metafield_quick_ship",
          "metafield_expert_pick",
          "metafield_product_availability",
          "metafield_reviews_average",
          "metafield_reviews_count",
          ...catalogPriceKeys,
        ],
      });
    }

    setupBreadcrumbContextHandoff() {
      this.productGrid = this.querySelector("#main-product-grid");

      if (!this.productGrid) return;

      this.handleProductClick = this.onProductClick.bind(this);
      this.productGrid.addEventListener("click", this.handleProductClick);
    }

    onProductClick(event) {
      const link = event.target.closest(
        "[data-product-url], [data-product-title-link]",
      );
      if (!link || !this.productGrid.contains(link)) return;

      const targetProductPath = this.getProductPath(link.getAttribute("href"));
      if (!targetProductPath) return;

      const breadcrumbContext = this.buildBreadcrumbContext(targetProductPath);
      if (!breadcrumbContext) return;

      this.writeBreadcrumbContext(breadcrumbContext);
    }

    getProductPath(url) {
      if (!url) return "";

      const parsedUrl = new URL(url, window.location.origin);

      if (!parsedUrl.pathname.startsWith("/products/")) {
        return "";
      }

      return parsedUrl.pathname;
    }

    buildBreadcrumbContext(targetProductPath) {
      const breadcrumbList = document.querySelector(
        "#breadcrumb .breadcrumb-list",
      );
      if (!breadcrumbList) return null;

      const crumbs = Array.from(
        breadcrumbList.querySelectorAll(".breadcrumb-item"),
      )
        .filter((item) => {
          if (item.classList.contains("breadcrumb-item--home")) return false;

          return (
            item.classList.contains("breadcrumb-item--vehicle") ||
            item.classList.contains("breadcrumb-item--parts")
          );
        })
        .map((item) => this.serializeBreadcrumbItem(item))
        .filter(Boolean);

      if (!crumbs.length) return null;

      return {
        targetProductPath,
        timestamp: Date.now(),
        crumbs,
      };
    }

    serializeBreadcrumbItem(item) {
      const title = item
        .querySelector("[itemprop='name']")
        ?.textContent?.trim();
      if (!title) return null;

      const typeClass = item.classList.contains("breadcrumb-item--parts")
        ? "breadcrumb-item--parts"
        : "breadcrumb-item--vehicle";

      return {
        title,
        url: item.querySelector("a[href]")?.getAttribute("href") || "",
        typeClass,
      };
    }

    writeBreadcrumbContext(value) {
      try {
        window.sessionStorage.setItem(
          this.breadcrumbStorageKey,
          JSON.stringify(value),
        );
      } catch (error) {
        console.error("Error setting breadcrumb sessionStorage:", error);
      }
    }
  }
  customElements.define("main-collection", MainCollection);
}
